import React, { useState, useEffect } from 'react';
import { Shield, Lock, AlertTriangle, Users, Settings, ChevronRight, FileText, CheckCircle, Clock, Table, BookOpen, ChevronDown, Server, FileCode, Key, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS, REQUEST_CONFIG } from './compliance/config';
import ISO27001 from './compliance/ISO27001';
import ISO27001RequirementsMatrix from './compliance/ISO27001RequirementsMatrix';
import ISO27001Section4 from './compliance/ISO27001Section4';
import ISO27001Section5 from './compliance/ISO27001Section5';
import ISO27001Section6 from './compliance/ISO27001Section6';
import SOC2 from './compliance/SOC2';
import SOC2RequirementsMatrix from './compliance/SOC2RequirementsMatrix';
import InfrastructureCloudSecurity from './compliance/InfrastructureCloudSecurity';
import CodeOverview from './compliance/code/CodeOverview';
import CodeQuality from './compliance/code/CodeQuality';
import CodeAnalysis from './compliance/code/CodeAnalysis';
import ApplicationAnalysis from './compliance/code/ApplicationAnalysis';
import ApiTesting from './compliance/code/ApiTesting';

/**
 * SecurityOverview component providing a side navigation panel for different security sections
 * @returns {JSX.Element}
 */
function SecurityOverview() {
  // State to track active section
  const [activeSection, setActiveSection] = useState('overview');
  // State for secret detection report
  const [secretReport, setSecretReport] = useState(null);
  const [secretReportLoading, setSecretReportLoading] = useState(false);
  // If there was a 'vulnerabilities' value in localStorage, migrate it to 'code'
  React.useEffect(() => {
    if (activeSection === 'vulnerabilities') {
      setActiveSection('code');
    }
  }, [activeSection]);
  
  // Fetch secret detection report when overview is shown
  useEffect(() => {
    if (activeSection === 'overview') {
      fetchSecretReport();
    }
  }, [activeSection]);
  
  // Function to fetch the Secret Detection report
  const fetchSecretReport = async () => {
    try {
      setSecretReportLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.SECRET_DETECTION_REPORT}`,
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      if (response.data && response.data.status === 'success') {
        setSecretReport(response.data);
      } else {
        setSecretReport({
          status: 'error',
          message: response.data.message || 'Failed to fetch report'
        });
      }
      setSecretReportLoading(false);
    } catch (error) {
      setSecretReport({
        status: 'error',
        message: error.message || 'An error occurred'
      });
      setSecretReportLoading(false);
    }
  };
  
  // Calculate report stats
  const calculateSecretStats = (report) => {
    // Check if report exists and has the correct structure
    if (!report || !report.report) {
      return { total: 0, high: 0, medium: 0, low: 0, info: 0 };
    }
    
    const secrets = report.report.vulnerabilities || report.report;
    if (!Array.isArray(secrets)) {
      return { total: 0, high: 0, medium: 0, low: 0, info: 0 };
    }
    
    const stats = { total: secrets.length, high: 0, medium: 0, low: 0, info: 0 };
    
    secrets.forEach(secret => {
      if (secret.severity) {
        const severity = secret.severity.toLowerCase();
        if (severity === 'critical' || severity === 'high') {
          stats.high++;
        } else if (severity === 'medium') {
          stats.medium++;
        } else if (severity === 'low') {
          stats.low++;
        } else {
          stats.info++;
        }
      }
    });
    
    return stats;
  };
  // State to track expanded menu items
  const [expandedItems, setExpandedItems] = useState(['iso27001', 'code']);

  // Navigation items with icons
  const navItems = [
    { id: 'overview', label: 'Overview', icon: <Shield size={20} /> },
    { 
      id: 'iso27001', 
      label: 'ISO 27001', 
      icon: <FileText size={20} />,
      children: [
        { id: 'iso27001-overview', label: 'Overview', icon: <Shield size={16} /> },
        { id: 'iso27001-matrix', label: 'Requirements Matrix', icon: <Table size={16} /> },
        { 
          id: 'iso27001-guides', 
          label: 'Section Guides', 
          icon: <BookOpen size={16} />,
          children: [
            { id: 'iso27001-section4', label: 'Section 4 Guide', icon: <FileText size={16} /> },
            { id: 'iso27001-section5', label: 'Section 5 Guide', icon: <FileText size={16} /> },
            { id: 'iso27001-section6', label: 'Section 6 Guide', icon: <FileText size={16} /> }
          ]
        }
      ]
    },
    { 
      id: 'code', 
      label: 'Code', 
      icon: <AlertTriangle size={20} />,
      children: [
        { id: 'code-overview', label: 'Overview', icon: <Shield size={16} /> },
        { id: 'code-quality', label: 'Code Quality', icon: <FileCode size={16} /> },
        { id: 'code-analysis', label: 'Code Analysis', icon: <Shield size={16} /> },
        { id: 'application-analysis', label: 'Application Analysis', icon: <Server size={16} /> },
        { id: 'api-testing', label: 'Testing', icon: <CheckCircle size={16} /> }
      ]
    },
    { 
      id: 'soc2', 
      label: 'SOC 2', 
      icon: <CheckCircle size={20} />,
      children: [
        { id: 'soc2-overview', label: 'Overview', icon: <Shield size={16} /> },
        { id: 'soc2-matrix', label: 'Requirements Matrix', icon: <Table size={16} /> }
      ]
    },
    { id: 'infrastructure-cloud', label: 'Infrastructure & Cloud Security', icon: <Server size={20} /> },
    { id: 'code', label: 'Code', icon: <AlertTriangle size={20} /> },
    { id: 'access', label: 'Access Control', icon: <Users size={20} /> },
    { id: 'encryption', label: 'Encryption', icon: <Lock size={20} /> },
    { id: 'settings', label: 'Settings', icon: <Settings size={20} /> },
  ];

  // Toggle expanded state for an item
  const toggleExpand = (itemId) => {
    setExpandedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId) 
        : [...prev, itemId]
    );
  };

  // Handle item click
  const handleItemClick = (itemId, hasChildren) => {
    if (hasChildren) {
      toggleExpand(itemId);
    } else {
      setActiveSection(itemId);
    }
  };

  // Render the content based on active section
  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div>
            <h1 className="text-3xl font-bold mb-6 text-gray-800">Security Overview</h1>
            
            {/* Compliance Status Cards */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">Compliance Status</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-300 border border-gray-100">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-blue-50 text-blue-600 mr-4">
                      <FileText size={24} />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-800">ISO 27001</h2>
                  </div>
                  <p className="text-gray-600 mb-4">Implementation progress for ISO 27001 compliance requirements.</p>
                  
                  <div className="space-y-3 mb-4">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">Technical Controls</span>
                        <span className="text-sm font-medium text-gray-700">92%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '92%' }}></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">Software Development</span>
                        <span className="text-sm font-medium text-gray-700">85%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">Integration Areas</span>
                        <span className="text-sm font-medium text-gray-700">78%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '78%' }}></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="h-3 w-3 rounded-full bg-green-500 mr-2"></div>
                      <span className="text-sm text-gray-600">Overall: 85% Complete</span>
                    </div>
                    <span className="text-sm text-gray-500">Last updated: Mar 24, 2025</span>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-300 border border-gray-100">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-purple-50 text-purple-600 mr-4">
                      <CheckCircle size={24} />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-800">SOC 2</h2>
                  </div>
                  <p className="text-gray-600 mb-4">Implementation progress for SOC 2 compliance requirements.</p>
                  
                  <div className="space-y-3 mb-4">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">Security</span>
                        <span className="text-sm font-medium text-gray-700">95%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '95%' }}></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">Availability</span>
                        <span className="text-sm font-medium text-gray-700">88%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '88%' }}></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">Processing Integrity</span>
                        <span className="text-sm font-medium text-gray-700">90%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '90%' }}></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">Confidentiality</span>
                        <span className="text-sm font-medium text-gray-700">92%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '92%' }}></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">Privacy</span>
                        <span className="text-sm font-medium text-gray-700">85%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="h-3 w-3 rounded-full bg-green-500 mr-2"></div>
                      <span className="text-sm text-gray-600">Overall: 90% Complete</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock size={14} className="mr-1" />
                      <span>Audit scheduled: Apr 15, 2025</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Security Status Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-300 border border-gray-100">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-full bg-blue-50 text-blue-600 mr-4">
                    <Shield size={24} />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800">Security Status</h2>
                </div>
                <p className="text-gray-600 mb-4">Your system is currently secure and up to date with the latest security patches.</p>
                <div className="mt-2 flex items-center">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-green-500 h-2.5 rounded-full" style={{ width: '92%' }}></div>
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-700">92%</span>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-300 border border-gray-100">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-full bg-purple-50 text-purple-600 mr-4">
                    <AlertTriangle size={24} />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800">Vulnerabilities</h2>
                </div>
                <p className="text-gray-600 mb-4">No critical vulnerabilities detected in your system.</p>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-sm text-gray-500">Last scan: Today</span>
                  <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">Run New Scan</button>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-300 border border-gray-100">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-full bg-indigo-50 text-indigo-600 mr-4">
                    <Key size={24} />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800">Secret Detection</h2>
                </div>
                {secretReportLoading ? (
                  <div className="flex justify-center items-center h-20">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500"></div>
                  </div>
                ) : secretReport && secretReport.status === 'success' ? (
                  <>
                    <p className="text-gray-600 mb-4">
                      {calculateSecretStats(secretReport).total === 0
                        ? "No exposed secrets detected in your codebase. Great job!"
                        : `${calculateSecretStats(secretReport).total} potential ${calculateSecretStats(secretReport).total === 1 ? 'secret' : 'secrets'} detected in your codebase.`
                      }
                    </p>
                    {calculateSecretStats(secretReport).total > 0 && (
                      <div className="flex space-x-3 mb-4">
                        {calculateSecretStats(secretReport).high > 0 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {calculateSecretStats(secretReport).high} High
                          </span>
                        )}
                        {calculateSecretStats(secretReport).medium > 0 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            {calculateSecretStats(secretReport).medium} Medium
                          </span>
                        )}
                        {calculateSecretStats(secretReport).low > 0 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            {calculateSecretStats(secretReport).low} Low
                          </span>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-gray-600 mb-4">Unable to load secret detection report.</p>
                )}
                <div className="flex justify-between items-center mt-2">
                  <span className="text-sm text-gray-500">
                    {secretReport && secretReport.timestamp ? `Last scan: ${new Date(secretReport.timestamp).toLocaleDateString()}` : 'No recent scan'}
                  </span>
                  <button 
                    onClick={fetchSecretReport} 
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                  >
                    <RefreshCw size={14} className="mr-1" />
                    Refresh
                  </button>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-300 border border-gray-100">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-full bg-green-50 text-green-600 mr-4">
                    <Users size={24} />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800">Access Control</h2>
                </div>
                <p className="text-gray-600 mb-4">5 users with admin access, 12 with standard access.</p>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">Manage Users</button>
              </div>
              
              <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-300 border border-gray-100">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-full bg-amber-50 text-amber-600 mr-4">
                    <Lock size={24} />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800">Encryption</h2>
                </div>
                <p className="text-gray-600 mb-4">All data is encrypted at rest and in transit using industry standards.</p>
                <div className="flex items-center mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                    TLS 1.3
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                    AES-256
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    RSA-2048
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      case 'iso27001-overview':
        return <ISO27001 />;
      case 'iso27001-matrix':
        return <ISO27001RequirementsMatrix />;
      case 'iso27001-section4':
        return <ISO27001Section4 />;
      case 'iso27001-section5':
        return <ISO27001Section5 />;
      case 'iso27001-section6':
        return <ISO27001Section6 />;
      case 'soc2-overview':
        return <SOC2 />;
      case 'soc2-matrix':
        return <SOC2RequirementsMatrix />;
      case 'infrastructure-cloud':
        return <InfrastructureCloudSecurity />;
      case 'code-overview':
        return <CodeOverview />;
      case 'code-quality':
        return <CodeQuality />;
      case 'code-analysis':
        return <CodeAnalysis />;
      case 'application-analysis':
        return <ApplicationAnalysis />;
      case 'api-testing':
        return <ApiTesting />;
      case 'access':
        return (
          <div>
            <h1 className="text-3xl font-bold mb-6 text-gray-800">Access Control</h1>
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-800">User Access Management</h2>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">Add New User</button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-medium">JD</span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">John Doe</div>
                            <div className="text-sm text-gray-500">john.doe@example.com</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Admin</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Today, 9:41 AM</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                        <button className="text-red-600 hover:text-red-900">Revoke</button>
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-purple-600 font-medium">JS</span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">Jane Smith</div>
                            <div className="text-sm text-gray-500">jane.smith@example.com</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Developer</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Yesterday, 4:23 PM</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                        <button className="text-red-600 hover:text-red-900">Revoke</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      case 'encryption':
        return (
          <div>
            <h1 className="text-3xl font-bold mb-6 text-gray-800">Encryption</h1>
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
                  <Lock size={24} />
                </div>
                <h2 className="text-xl font-semibold text-gray-800">Encryption Status</h2>
              </div>
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Data at Rest</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Encrypted
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4">All stored data is encrypted using AES-256 encryption.</p>
              </div>
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Data in Transit</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Encrypted
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4">All data transfers are secured with TLS 1.3 encryption.</p>
              </div>
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">End-to-End Encryption</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Enabled
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4">Sensitive communications are protected with end-to-end encryption.</p>
              </div>
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Key Management</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Secure
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4">Encryption keys are securely managed and rotated regularly.</p>
              </div>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">Manage Encryption Settings</button>
            </div>
          </div>
        );
      case 'settings':
        return (
          <div>
            <h1 className="text-3xl font-bold mb-6 text-gray-800">Security Settings</h1>
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">General Settings</h2>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-medium text-gray-800">Two-Factor Authentication</h3>
                    <p className="text-sm text-gray-600">Require 2FA for all admin users</p>
                  </div>
                  <div className="relative inline-block w-12 h-6 rounded-full bg-gray-200">
                    <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transform translate-x-6 transition-transform duration-200"></div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-medium text-gray-800">Session Timeout</h3>
                    <p className="text-sm text-gray-600">Automatically log out after inactivity</p>
                  </div>
                  <select className="block w-32 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm">
                    <option>15 minutes</option>
                    <option>30 minutes</option>
                    <option>1 hour</option>
                    <option>4 hours</option>
                  </select>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-medium text-gray-800">IP Restrictions</h3>
                    <p className="text-sm text-gray-600">Limit access to specific IP addresses</p>
                  </div>
                  <div className="relative inline-block w-12 h-6 rounded-full bg-gray-200">
                    <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200"></div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-medium text-gray-800">Password Policy</h3>
                    <p className="text-sm text-gray-600">Enforce strong password requirements</p>
                  </div>
                  <div className="relative inline-block w-12 h-6 rounded-full bg-blue-600">
                    <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transform translate-x-6 transition-transform duration-200"></div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-medium text-gray-800">Security Alerts</h3>
                    <p className="text-sm text-gray-600">Receive notifications for security events</p>
                  </div>
                  <div className="relative inline-block w-12 h-6 rounded-full bg-blue-600">
                    <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transform translate-x-6 transition-transform duration-200"></div>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-200">
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">Save Changes</button>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Function to render a navigation item
  const renderNavItem = (item, depth = 0) => {
    const isActive = activeSection === item.id;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const isParentOfActive = hasChildren && item.children.some(child => 
      child.id === activeSection || 
      (child.children && child.children.some(grandchild => grandchild.id === activeSection))
    );
    
    // Determine if this is a parent of the active section at any level of nesting
    const isAncestorOfActive = isParentOfActive || 
      (hasChildren && item.children.some(child => 
        child.children && child.children.some(grandchild => grandchild.id === activeSection)
      ));

    return (
      <div key={item.id} className="mb-1">
        <div
          className={`flex items-center px-3 py-2 rounded-lg cursor-pointer ${
            isActive 
              ? 'bg-purple-100 text-purple-700' 
              : isAncestorOfActive
                ? 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                : 'hover:bg-gray-100'
          }`}
          onClick={() => {
            if (hasChildren) {
              toggleExpand(item.id);
            } else {
              setActiveSection(item.id);
            }
          }}
          style={{ paddingLeft: `${depth * 12 + 12}px` }}
        >
          <span className="mr-3">{item.icon}</span>
          <span className={`flex-grow ${isActive || isAncestorOfActive ? 'font-medium' : ''}`}>{item.label}</span>
          {hasChildren && (
            <ChevronDown 
              size={16} 
              className={`transform transition-transform ${isExpanded ? 'rotate-180' : 'rotate-0'}`} 
            />
          )}
        </div>
        
        {hasChildren && isExpanded && (
          <div className="mt-1">
            {item.children.map(child => renderNavItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full bg-gray-50">
      {/* Side Navigation */}
      <div className="w-64 bg-white shadow-sm p-4 overflow-y-auto">
        <h2 className="text-xl font-bold mb-6 text-gray-800">Security Center</h2>
          <div className="space-y-1">
            {navItems.map((item) => renderNavItem(item))}
          </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 p-8 overflow-auto">
        {renderContent()}
      </div>
    </div>
  );
}

export default SecurityOverview;
